import { useCallback, useMemo } from 'react';

import { useAuthentication } from '@givebacks/api';
import { useBackerHasStudentFeesAccess } from '@givebacks/api/backer';
import { useSelectHasEitherCardTypes } from '@givebacks/api/core';
import type { ConsumerCheckoutCartsResponse } from '@givebacks/api/store';
import { useShowConsumerCheckout } from '@givebacks/api/store';
import { useForm } from '@givebacks/formy';
import { ShopToGiveEnrollmentForm } from 'core/Components';

import { ShopToGiveEnrollmentFormType } from '../../../../pages/Checkout/checkout.types';

const CheckoutShopToGiveEnrollmentSection = () => {
  const {
    hasStudentFeesAccess,

    isLoading: isStudentFeesInitialLoading,
    isEnabled: isStudentFeesAccessEnabled,
  } = useBackerHasStudentFeesAccess();

  const form = useForm<ShopToGiveEnrollmentFormType>(
    {
      initialValues: {
        terms: false,
      },
      validate: {
        enrollmentOption: (value) => {
          return value == null ? 'Please select an option to continue' : null;
        },
        terms: (value, values) => (!value && !!values.enrollmentOption ? true : null),
      },
    },
    {
      id: 'shop_to_give_enrollment',
    }
  );

  if (isStudentFeesInitialLoading && isStudentFeesAccessEnabled) return null;

  return (
    <ShopToGiveEnrollmentForm
      scrollerRef={form.formRef}
      isFeesUser={!!hasStudentFeesAccess}
      enrollmentOptionFieldProps={{
        ...form.getInputProps('enrollmentOption'),
        onChange: (value) => {
          (form.getInputProps('enrollmentOption').onChange as (value?: boolean) => void)(value);
          form.setFieldValue('terms', false);
        },
      }}
      checkboxFieldProps={form.getInputProps('terms', { type: 'checkbox' })}
      showAgreeToTermsCheckbox={form.values.enrollmentOption}
      enrollmentOptionError={form.errors.enrollmentOption}
      agreeToTermsError={form.errors.terms}
    />
  );
};

const WrappedCheckoutShopToGiveEnrollmentSection = ({ checkoutId }: { checkoutId: string }) => {
  const { data: isPaymentMethodNeeded } = useShowConsumerCheckout({
    checkoutId,
    select: useCallback(
      (data: ConsumerCheckoutCartsResponse) => data.checkout.payment_method_needed,
      []
    ),
    placeholderData: (prev) => prev,
  });
  const { isAuthenticated, isLoading: isAuthenticatedLoading } = useAuthentication();
  const { data: isEnrolled, isLoading: isEnrolledLoading } = useSelectHasEitherCardTypes();

  const isLoading = useMemo(() => {
    return isAuthenticatedLoading || isEnrolledLoading;
  }, [isAuthenticatedLoading, isEnrolledLoading]);

  const showEnrollmentSection = useMemo(() => {
    // Don't show if payment method is not needed
    if (!isPaymentMethodNeeded) return false;

    // Show for unauthenticated users
    if (!isAuthenticated) return true;

    // For authenticated users, only show if they don't have a partner card
    return !isEnrolled;
  }, [isPaymentMethodNeeded, isAuthenticated, isEnrolled]);

  if (!showEnrollmentSection || isLoading) return null;

  return <CheckoutShopToGiveEnrollmentSection />;
};

export { WrappedCheckoutShopToGiveEnrollmentSection as CheckoutShopToGiveEnrollmentSection };
